import {
	GridStackEngine,
	type GridStackMoveOpts,
	type GridStackNode,
	type GridStackPosition,
} from 'gridstack';

/** GridStack v13 node internals this engine reads (not in its public typings). */
interface DragNode extends GridStackNode {
	_id?: number;
	_moving?: boolean;
	_updating?: boolean;
	/** Card this drag already jumped below, and the cursor row where it did. */
	_jeptoPassed?: { id?: number; fromY: number };
}

type FixCollisions = (
	this: GridStackEngine,
	node: GridStackNode,
	nn?: GridStackPosition,
	collide?: GridStackNode,
	opt?: GridStackMoveOpts
) => boolean;

const baseFixCollisions = (
	GridStackEngine.prototype as unknown as { _fixCollisions?: FixCollisions }
)._fixCollisions;

/**
 * Float-mode engine for the report grid (JEP-10769): cards stay where they
 * are dropped, so a card can sit lower than its neighbours.
 *
 * Plain float mode always pushes a card you drag onto further down, so
 * dragging a card under the last card made that card run away (JEP-10485).
 * GridStack only "skips past" the covered card in gravity mode. This engine
 * does the same in float mode:
 * - dragging down over a card moves that card up into the space you left
 *   (when it fits) and places the dragged card under it;
 * - after jumping below a card, small upward cursor moves don't bounce back
 *   over it until the cursor goes above the row where the jump happened.
 */
export class JeptoGridStackEngine extends GridStackEngine {
	// fallow-ignore-next-line unused-class-member
	override beginUpdate(node: GridStackNode): GridStackEngine {
		const dragNode = node as DragNode;
		if (!dragNode._updating) {
			delete dragNode._jeptoPassed;
		}
		return super.beginUpdate(node);
	}

	/** Overrides GridStack's internal collision handler (called by `moveNode`). */
	// fallow-ignore-next-line unused-class-member
	_fixCollisions(
		node: GridStackNode,
		nn: GridStackPosition = node,
		collide?: GridStackNode,
		opt: GridStackMoveOpts = {}
	): boolean {
		const dragNode = node as DragNode;
		const covered = this.#coveredDuringFloatDrag(
			dragNode,
			nn,
			collide,
			opt
		);
		if (covered && nn.y! > node.y! && covered.y! > node.y!) {
			return this.#dropBelow(dragNode, nn, covered);
		}
		if (covered && this.#isStillBelow(dragNode, nn, covered)) {
			return true;
		}
		return this.#baseFixCollisions(node, nn, covered ?? collide, opt);
	}

	/** The unlocked card a float-mode drag is over, or undefined when GridStack's own handling applies. */
	#coveredDuringFloatDrag(
		node: DragNode,
		nn: GridStackPosition,
		collide: GridStackNode | undefined,
		opt: GridStackMoveOpts
	): DragNode | undefined {
		if (!this.float || !node._moving || opt.nested) {
			return undefined;
		}
		const covered = collide ?? this.collide(node, nn);
		return covered?.locked ? undefined : covered;
	}

	#baseFixCollisions(
		node: GridStackNode,
		nn: GridStackPosition,
		collide: GridStackNode | undefined,
		opt: GridStackMoveOpts
	): boolean {
		return baseFixCollisions?.call(this, node, nn, collide, opt) ?? false;
	}

	/** Lets the covered card take the dragged card's old spot when it fits, then places the dragged card under it. */
	#dropBelow(node: DragNode, nn: GridStackPosition, covered: DragNode) {
		node._jeptoPassed = { id: covered._id, fromY: nn.y! };
		if (!this.collide(covered, { ...covered, y: node.y }, node)) {
			this.moveNode(covered, {
				...covered,
				y: node.y,
				skip: node,
				nested: true,
				pack: false,
			});
		}
		return this.moveNode(node, {
			...nn,
			y: covered.y! + covered.h!,
			nested: true,
			pack: false,
		});
	}

	/** True while the cursor hasn't gone back above the row where the card jumped below `covered`. */
	#isStillBelow(node: DragNode, nn: GridStackPosition, covered: DragNode) {
		const passed = node._jeptoPassed;
		return (
			nn.y! < node.y! &&
			passed?.id === covered._id &&
			nn.y! >= passed.fromY
		);
	}
}
